import { ChartHeaderProps, DoughnutChartProps, MSLineProps, Series } from 'components/charts'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { EventTotalPeriodStatsDTO, OrderStatsRequestDTO, SalesDTOObject, ViewerEventEnums } from 'models'
import { FilterDTO } from 'models/FilterDTO'
import moment from 'moment'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { STATISTICS_OVERVIEW_STORE, statsOverviewExpensesEvents, statsOverviewExpensesEventsLabels, statsOverviewInteractionEvents, statsOverviewInteractionEventsLabels } from 'store/StatisticsOverviewStore'
import { STATISTICS_PAGE_STORE, statsColorPallette } from 'store/StatisticsPageStore'
import { Api, formatPrice } from 'utilities'
import { TotalsByEventTypeMap } from './StatisticsOverviewBarChartStore.types'

export const STATISTICS_OVERVIEW_BAR_CHART_STORE = 'STATISTICS_OVERVIEW_BAR_CHART_STORE'

export class StatisticsOverviewBarChartStore {
  private rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable private totalsByEventTypeMap: TotalsByEventTypeMap = new Map()
  @observable private totalSalesForPeriod: SalesDTOObject = {}
  @observable private expandableSectionSelectedEvents: Set<ViewerEventEnums> =
    new Set(statsOverviewExpensesEvents)

  @action private setTotalsByEventTypeMap = (map: TotalsByEventTypeMap): void => {
    this.totalsByEventTypeMap = map
  }

  @action private setTotalSalesForPeriod = (obj: SalesDTOObject): void => {
    this.totalSalesForPeriod = obj
  }

  @action private setInitExpandableSectionSelectedEvents = (): void => {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        isByExpensesTabSelected
      }
    } = this.rootStore

    this.expandableSectionSelectedEvents = new Set(
      isByExpensesTabSelected
        ? statsOverviewExpensesEvents
        : statsOverviewInteractionEvents
    )
  }

  @action onExpandableSectionEventCheckboxClick = (eventType: ViewerEventEnums): void => {
    if (this.isEventSelected(eventType)) {
      this.expandableSectionSelectedEvents.delete(eventType)
    } else {
      this.expandableSectionSelectedEvents.add(eventType)
    }
  }

  private handleEventsPeriodStats = (
    data: [EventTotalPeriodStatsDTO[], SalesDTOObject]
  ): void => {
    const [events, salesDtoObject] = data
    const sales = Object.values(salesDtoObject)

    const totalsByEventTypeMap: TotalsByEventTypeMap = new Map()
    events.forEach(({ eventType, periodStats }) => {
      const eventAddToCart = eventType === ViewerEventEnums.ADD_TO_CART
      const stats = Object.values(periodStats)
      const totalCount = stats.reduce((sum, stat) => sum + stat.count, 0) 
      // sales.reduce((sum, { addToCart }) => sum + (addToCart || 0), 0)
      const totalRevenue = stats.reduce((sum, stat) => sum + stat.revenue, 0)
      totalsByEventTypeMap.set(eventType, {
        periodStats,
        totalCount,
        totalRevenue
      })
    })

    totalsByEventTypeMap.set(ViewerEventEnums.PURCHASE, {
      periodStats: {},
      totalCount: sales.reduce((sum, { count }) => sum + (count || 0), 0),
      totalRevenue: sales.reduce((sum, { commission }) => sum + (commission || 0), 0)
    })
    // console.log(sales.reduce((sum, { addToCart }) => sum + (addToCart || 0), 0))
    // totalsByEventTypeMap.set(ViewerEventEnums.ADD_TO_CART, {
    //   periodStats: {},
    //   totalCount: sales.reduce((sum, { addToCart }) => sum + (addToCart || 0), 0),
    //   totalRevenue: 0
    // })

    this.setTotalsByEventTypeMap(totalsByEventTypeMap)
    this.setTotalSalesForPeriod(salesDtoObject)
  }

  getEventsPeriodStats = async (): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_OVERVIEW_STORE]: {
        period
      },
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      }
    } = this.rootStore

    setLoadingOn()

    Promise.all([
      Api.post<EventTotalPeriodStatsDTO[], FilterDTO>(
        '/stats/summary/totalPeriod/events',
        {
          ...period,
          eventTypes: Object.values(ViewerEventEnums),
          brandIds: selectedStoreIds
        },
        'statsApi'
      ),
      Api.post<SalesDTOObject, OrderStatsRequestDTO>(
        '/order/statistic/period',
        {
          startDate: moment(period.startDate != null ? period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(period.endDate != null ? period.endDate : moment()).format('YYYY-MM-DD'),
          brands: selectedStoreIds
        }
      )
    ])
      .then(data => {
        this.handleEventsPeriodStats(data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  onStatisticsOverviewTabChange = (): void => {
    this.setInitExpandableSectionSelectedEvents()
  }

  @computed private get totalEventsRevenue (): string {
    return formatPrice(
      Array.from(this.totalsByEventTypeMap.values())
        .reduce((sum, { totalRevenue }) => sum + totalRevenue, 0)
    )
  }

  @computed private get totalEventsCount (): number {
    return Array.from(this.totalsByEventTypeMap.values())
      .reduce((sum, { totalCount }) => sum + totalCount, 0)
  }

  @computed get chartHeaderProps (): ChartHeaderProps {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        isByExpensesTabSelected,
        projectsCount
      }
    } = this.rootStore

    const total: string = isByExpensesTabSelected
      ? 'Total: ' + this.totalEventsRevenue
      : this.totalEventsCount + ' interactions'

    const events: ViewerEventEnums[] = isByExpensesTabSelected
      ? statsOverviewExpensesEvents
      : statsOverviewInteractionEvents
    const labels = isByExpensesTabSelected
      ? statsOverviewExpensesEventsLabels
      : statsOverviewInteractionEventsLabels
    const sections = !isByExpensesTabSelected 
    ? new Map(
      events
      .filter(eventType => {
       return eventType !== ViewerEventEnums.BLOB_ATTACHED &&
              eventType !== ViewerEventEnums.BLOB_DETACHED &&
              eventType !== ViewerEventEnums.POSTER_ATTACHED
      })
      .map(eventType => [
        labels[eventType],
        isByExpensesTabSelected
          ? formatPrice(this.totalsByEventTypeMap.get(eventType)?.totalRevenue || 0)
          : this.totalsByEventTypeMap.get(eventType)?.totalCount || 0
      ])
    ) 
    : new Map(
      events.map(eventType => [
        labels[eventType],
        isByExpensesTabSelected
          ? formatPrice(this.totalsByEventTypeMap.get(eventType)?.totalRevenue || 0)
          : this.totalsByEventTypeMap.get(eventType)?.totalCount || 0
      ])
    )

    return {
      projectsCount,
      total,
      sections
    }
  }

  @computed get eventsDoughnutProps (): DoughnutChartProps {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        isByExpensesTabSelected
      }
    } = this.rootStore

    const type: string = isByExpensesTabSelected
      ? 'EXPENSES'
      : 'INTERACTIONS'
    const total: number | string = isByExpensesTabSelected
      ? this.totalEventsRevenue
      : this.totalEventsCount
    const eventLabels = isByExpensesTabSelected
      ? statsOverviewExpensesEventsLabels
      : statsOverviewInteractionEventsLabels

    const data: DoughnutChartProps['data'] = Array.from(this.totalsByEventTypeMap.entries())
      .map(([eventType, { totalCount, totalRevenue }]) => {
        return {
          label: eventLabels[eventType],
          value: isByExpensesTabSelected
            ? String(totalRevenue)
            : String(totalCount)
        }
      })

    return {
      centerLabel: `TOTAL ${type} <br/> ${total}`,
      data
    }
  }

  @computed get eventsDoughnutLegend (): {
    eventType: ViewerEventEnums,
    color: string,
    label: string,
    value: string
  }[] {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        isByExpensesTabSelected
      }
    } = this.rootStore

    const eventTypes: ViewerEventEnums[] = isByExpensesTabSelected
      ? statsOverviewExpensesEvents
      : statsOverviewInteractionEvents
    const labelsObject = isByExpensesTabSelected
      ? statsOverviewExpensesEventsLabels
      : statsOverviewInteractionEventsLabels

    return eventTypes.map((eventType, index) => {
      const totals = this.totalsByEventTypeMap.get(eventType)
      const value = isByExpensesTabSelected
        ? formatPrice(totals?.totalRevenue || 0)
        : String(totals?.totalCount || 0)
      return {
        eventType,
        color: statsColorPallette[index].fill,
        label: labelsObject[eventType],
        value
      }
    })
  }

  @observable isEventSelected = computedFn(
    (eventType: ViewerEventEnums): boolean => {
      return this.expandableSectionSelectedEvents.has(eventType)
    }
  )

  @computed get eventsPeriodChartProps (): MSLineProps {
    const {
      [STATISTICS_OVERVIEW_STORE]: {
        dayLabelKeys,
        isByExpensesTabSelected
      }
    } = this.rootStore
    if (!dayLabelKeys) return null

    const series: Series[] = []

    const eventTypes: ViewerEventEnums[] = isByExpensesTabSelected
      ? statsOverviewExpensesEvents
      : statsOverviewInteractionEvents
    const eventLabels = isByExpensesTabSelected
      ? statsOverviewExpensesEventsLabels
      : statsOverviewInteractionEventsLabels

    eventTypes.forEach((eventType, index) => {
      if (!this.isEventSelected(eventType)) return
      const periodStats = this.totalsByEventTypeMap.get(eventType)?.periodStats || {}
      const data = []
      dayLabelKeys.forEach(({ label }) => {
        let value
        switch (eventType) {
          case ViewerEventEnums.PURCHASE: {
            value = isByExpensesTabSelected
              ? this.totalSalesForPeriod[label]?.commission || 0
              : this.totalSalesForPeriod[label]?.count || 0
            break
          }
          case ViewerEventEnums.ADD_TO_CART: {
            value = this.totalSalesForPeriod[label]?.addToCart || 0
            break
          }
          default: {
            value = isByExpensesTabSelected
              ? periodStats[label]?.revenue || 0
              : periodStats[label]?.count || 0
            break
          }
        }
        data.push({ value })
      })
      series.push({
        seriesname: eventLabels[eventType],
        data,
        anchorBgColor: statsColorPallette[index].fill,
        color: statsColorPallette[index].fill
      })
    })

    const headerLabel = `Events: ${series.length
      ? eventTypes.length === series.length
        ? 'All'
        : series.map(({ seriesname }) => seriesname).join(', ')
      : 'None'}`

    return {
      yAxisName: isByExpensesTabSelected ? 'Expenses USD' : 'Interactions',
      numberPrefix: isByExpensesTabSelected ? '$' : '',
      dataset: series,
      categories: [
        { category: dayLabelKeys }
      ],
      height: '55%',
      headerLabel,
      hideLegend: true
    }
  }
}
